import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { endpoints, type JobItem } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/utils";

export function JobsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["jobs-page"], queryFn: endpoints.jobs, refetchInterval: 5_000 });
  const [selected, setSelected] = useState<JobItem | null>(null);
  const pollNow = useMutation({
    mutationFn: (id: string) => endpoints.pollJob(id),
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: ["jobs-page"] });
      setSelected(job);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Job RENTRI</h1>
        <p className="text-sm text-muted-foreground">Transazioni asincrone in polling automatico</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Job recenti</CardTitle></CardHeader>
        <CardContent className="p-0">
          {q.isLoading && <CenteredLoader />}
          {q.error && <ErrorState error={q.error} />}
          {q.data && q.data.length === 0 && <EmptyState title="Nessun job" description="I job vengono creati quando RENTRI risponde 202 a un POST" />}
          {q.data && q.data.length > 0 && (
            <Table>
              <THead><TR><TH>Tipo</TH><TH>TX RENTRI</TH><TH>Stato</TH><TH>Tentativi</TH><TH>Prox poll</TH><TH>Aggiornato</TH></TR></THead>
              <TBody>
                {q.data.map((j: JobItem) => (
                  <TR key={j.id} className="cursor-pointer" onClick={() => setSelected(j)}>
                    <TD className="font-medium">{j.job_type}</TD>
                    <TD className="font-mono text-xs">{j.rentri_transaction_id ?? "—"}</TD>
                    <TD><JobBadge status={j.status} /></TD>
                    <TD className="text-center">{j.attempts}</TD>
                    <TD className="text-xs text-muted-foreground">{j.next_poll_at ? formatDate(j.next_poll_at) : "—"}</TD>
                    <TD className="text-xs text-muted-foreground">{formatDate(j.updated_at)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Dettaglio job</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Field k="ID" v={selected.id} />
                <Field k="Tipo" v={selected.job_type} />
                <Field k="Stato" v={<JobBadge status={selected.status} />} />
                <Field k="TX RENTRI" v={selected.rentri_transaction_id ?? "—"} mono />
                <Field k="Tentativi" v={String(selected.attempts)} />
                <Field k="Correlation ID" v={selected.correlation_id ?? "—"} mono />
              </div>
              {(selected.status === "pending" || selected.status === "running") && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pollNow.isPending}
                  onClick={() => pollNow.mutate(selected.id)}
                >
                  {pollNow.isPending ? "Aggiornamento…" : "Aggiorna stato ora"}
                </Button>
              )}
              {selected.error && (
                <div className="rounded bg-destructive/10 p-3 text-sm text-destructive">
                  <strong>Errore:</strong> {selected.error}
                </div>
              )}
              {selected.result_payload && (
                <div>
                  <h4 className="mb-1 text-sm font-medium">Result payload</h4>
                  <pre className="overflow-x-auto rounded bg-muted/50 p-3 text-xs">{JSON.stringify(selected.result_payload, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JobBadge({ status }: { status: string }) {
  const v = status === "done" ? "success"
    : status === "failed" || status === "expired" ? "destructive"
    : status === "running" ? "default" : "warning";
  return <Badge variant={v as never}>{status}</Badge>;
}

function Field({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase text-muted-foreground">{k}</span>
      <span className={mono ? "font-mono text-xs break-all" : ""}>{v}</span>
    </div>
  );
}
